import { EndpointBuilder } from '@reduxjs/toolkit/query';
import { DataGridState } from '@sl/react-datafabric-components';
import { convertStateToParams } from '../common/redux/utils';

export type ListResponse<T> = {
  data: T[];
  meta: {
    hasNextPage: boolean;
    hasPreviousPage: boolean;
    itemCount: number;
    page: number;
    pageCount: number;
    pageSize: number;
  };
};

abstract class BaseReduxService<
  T extends any & { id: string } = any & { id: string }
> {
  private moduleName: string;
  private baseUrl: string;

  constructor(baseUrl: string, moduleName: string) {
    this.baseUrl = `${baseUrl}${moduleName}`;
    this.moduleName = moduleName;
  }

  // To preserve the type of the object, we need to use the generic type T for name and create endpoints using this function this lets TS know what redux toolkit is producing
  private makeEndpoint<K extends string, T>(name: K, value: T) {
    return { [name]: value } as Record<K, T>;
  }

  createEndpoints<N extends string>(
    builder: EndpointBuilder<any, any, any>,
    prefix: N
  ) {
    const capitalizedModuleName = this.capitalizedModuleName();
    const singleItemTag = `${capitalizedModuleName}Item`; // New tag for single items

    return {
      ...this.makeEndpoint(
        `${prefix}FetchItems`,
        builder.query<ListResponse<T>, DataGridState<Partial<T>>>({
          query: (gridState: any) => {
            const processedState = convertStateToParams(gridState);
            return {
              url: this.baseUrl,
              method: 'GET',
              params: processedState,
            };
          },
          providesTags: [capitalizedModuleName], // Provide the general tag
        })
      ),

      ...this.makeEndpoint(
        `${prefix}FetchItemById`,
        builder.query<{ data: T }, string>({
          query: (id: string) => `${this.baseUrl}/${id}`,
          providesTags: (result, error, id) => [{ type: singleItemTag, id }], // Provide the single item tag
        })
      ),

      ...this.makeEndpoint(
        `${prefix}CreateItem`,
        builder.mutation<{ data: T }, Partial<T>>({
          query: (newItem: any) => ({
            url: this.baseUrl,
            method: 'POST',
            body: newItem,
          }),
          invalidatesTags: [capitalizedModuleName], // Invalidate the general tag
        })
      ),

      ...this.makeEndpoint(
        `${prefix}UpdateItem`,
        builder.mutation<{ data: T }, Partial<T> & { id: string }>({
          query: ({ id, ...values }: Partial<T> & { id: string }) => ({
            url: `${this.baseUrl}/${id}`,
            method: 'PUT',
            body: values,
          }),
          invalidatesTags: (result, error, arg) => [
            { type: singleItemTag, id: arg.id },
            capitalizedModuleName,
          ],
        })
      ),

      ...this.makeEndpoint(
        `${prefix}DeleteItem`,
        builder.mutation<void, string>({
          query: (id: string) => ({
            url: `${this.baseUrl}/${id}`,
            method: 'DELETE',
          }),
          invalidatesTags: (result, error, id) => [
            { type: singleItemTag, id },
            capitalizedModuleName,
          ],
        })
      ),

      ...this.makeEndpoint(
        `${prefix}FetchQueryData`,
        builder.query({
          query: (data: { layerID: string; query: any }) => ({
            url: `${this.baseUrl}/query/${data.layerID}`,
            method: 'POST',
            body: data.query,
          }),
          providesTags: [capitalizedModuleName],
        })
      ),
    };
  }

  private capitalizedModuleName() {
    return `${
      this.moduleName.charAt(0).toUpperCase() + this.moduleName.slice(1)
    }`;
  }
}

export default BaseReduxService;
